import { FOR_FUN_LABEL } from "../InProgressMatches/matchDisplay";

/** Default opening length the Mobile page previews. */
export const FIRST_TEN_COUNT = 10;

/** Bounds for the opening-length field on the Mobile page. */
export const PREVIEW_COUNT_MIN = 1;
export const PREVIEW_COUNT_MAX = 50;

/** Deterministic draws used to pick a typical opening. */
const TRIALS = 80;

export const RANK_ORDER = [
  "IRON",
  "BRONZE",
  "UNRANKED",
  "SILVER",
  "GOLD",
  "PLATINUM",
  "DIAMOND",
  "LEGEND",
  "GRANDLEGEND",
  "IDOLIZED",
];

/** Per-rank input map → numbers, or null when any rank is blank or not > 0. */
export function readRankWeights(inputMap) {
  const weights = {};
  for (const rank of RANK_ORDER) {
    const raw = String(inputMap?.[rank] ?? "").trim();
    const value = Number(raw);
    if (raw === "" || !Number.isFinite(value) || value <= 0) {
      return null;
    }
    weights[rank] = value;
  }
  return weights;
}

export function rankWeightsDiffer(saved, draft) {
  if (!saved || !draft) return false;
  return RANK_ORDER.some((rank) => Number(saved[rank]) !== Number(draft[rank]));
}

/** Whole number from PREVIEW_COUNT_MIN to PREVIEW_COUNT_MAX, or null. */
export function readPreviewCount(raw) {
  const text = String(raw ?? "").trim();
  const value = Number(text);
  if (
    text === "" ||
    !Number.isInteger(value) ||
    value < PREVIEW_COUNT_MIN ||
    value > PREVIEW_COUNT_MAX
  ) {
    return null;
  }
  return value;
}

const positive = (value, fallback = 1) => {
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : fallback;
};

/**
 * Old-pagent feed factor: rate ^ ageSteps. ageSteps is how many 15-minute
 * blocks the pagent has been open. 0 steps (or a missing age) is 1.
 */
export function oldPagentRateFactor(rate, ageSteps) {
  const steps = Math.max(0, Math.floor(Number(ageSteps) || 0));
  return positive(rate) ** steps;
}

const bucketSortKey = (bucket) =>
  [
    bucket?.rank || "",
    bucket?.categoryKey || "",
    bucket?.isGroupDuet ? 1 : 0,
    bucket?.isContest ? 1 : 0,
    Number.isFinite(Number(bucket?.ageSteps)) ? Number(bucket.ageSteps) : 0,
    bucket?.count || 0,
  ].join("|");

/** mulberry32. Returns values in [0, 1). */
const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const rankCounts = (ranks) => {
  const counts = new Map();
  ranks.forEach((rank) => counts.set(rank, (counts.get(rank) || 0) + 1));
  return counts;
};

const countDistance = (counts, mean) => {
  let distance = 0;
  const seen = new Set(counts.keys());
  mean.forEach((value, rank) => {
    seen.add(rank);
  });
  seen.forEach((rank) => {
    distance += Math.abs((counts.get(rank) || 0) - (mean.get(rank) || 0));
  });
  return distance;
};

/**
 * A typical opening of ranks on the post-energy feed.
 *
 * Matches the voting feed: contest pagents sort ahead of everything else.
 * Every other pagent scores
 * rankWeight × categoryFeedWeight × groupDuetWeight ×
 * oldMatchBoost^ageSteps × U. ageSteps is how many 15-minute blocks the
 * pagent has been open; 0 leaves the old-pagent rate at 1.
 * A category with a full-energy (post-energy) rank override uses that table
 * instead of `rankWeights`. Several seeded draws are taken and the one whose
 * rank mix is closest to the average is shown, so the line tracks the chance
 * of each rank rather than a single unlucky shuffle. The seeds are fixed, so
 * the same pool and weights always produce the same line, and editing a
 * weight is the "if you save" preview.
 *
 * @param {Object} options
 * @param {Array} options.buckets - { rank, categoryKey, isGroupDuet, isContest, ageSteps, count }
 * @param {number} [options.forFunWeight] - Weight for For fun buckets. They
 *   are not a public rank, so they do not use `rankWeights`. Defaults to 1.
 * @param {Object} options.rankWeights - post-energy (full-energy) weights
 * @param {number} [options.count] - opening length; defaults to FIRST_TEN_COUNT
 * @returns {string[]} up to `count` rank keys
 */
export function firstTenRanks({
  buckets = [],
  rankWeights = {},
  forFunWeight = 1,
  categoryRankWeights = {},
  categoryFeedWeights = {},
  groupDuetFeedWeight = 1,
  oldMatchFeedBoost = 1,
  count = FIRST_TEN_COUNT,
} = {}) {
  const take = Math.min(
    PREVIEW_COUNT_MAX,
    Math.max(PREVIEW_COUNT_MIN, Math.floor(Number(count)) || FIRST_TEN_COUNT)
  );
  const groupWeight = positive(groupDuetFeedWeight);
  const oldBoost = positive(oldMatchFeedBoost);
  const prepared = [];

  [...buckets]
    .sort((a, b) => bucketSortKey(a).localeCompare(bucketSortKey(b)))
    .forEach((bucket) => {
      const bucketCount = Math.max(0, Math.floor(Number(bucket?.count) || 0));
      const rank = bucket?.rank || "UNKNOWN";
      const categoryKey = bucket?.categoryKey || null;
      const isContest = Boolean(bucket?.isContest);
      const isGroupDuet = Boolean(bucket?.isGroupDuet);
      const ageSteps = Math.max(0, Math.floor(Number(bucket?.ageSteps) || 0));
      const override = categoryKey
        ? categoryRankWeights?.[categoryKey]?.fullEnergy
        : null;
      const rankWeight =
        rank === FOR_FUN_LABEL
          ? positive(forFunWeight)
          : positive(override?.[rank] ?? rankWeights[rank]);
      const categoryWeight = categoryKey
        ? positive(categoryFeedWeights?.[categoryKey])
        : 1;
      const weight =
        rankWeight *
        categoryWeight *
        (isGroupDuet ? groupWeight : 1) *
        oldPagentRateFactor(oldBoost, ageSteps);

      for (let i = 0; i < bucketCount; i += 1) {
        prepared.push({ rank, isContest, weight });
      }
    });

  if (prepared.length === 0) return [];

  const draw = (seed) => {
    const random = mulberry32(seed);
    return prepared
      .map((item) => ({
        rank: item.rank,
        isContest: item.isContest,
        score: item.isContest ? random() : item.weight * (random() || 1e-12),
      }))
      .sort((a, b) => {
        if (a.isContest !== b.isContest) return a.isContest ? -1 : 1;
        return b.score - a.score;
      })
      .slice(0, take)
      .map((item) => item.rank);
  };

  const totals = new Map();
  const trials = [];
  for (let trial = 0; trial < TRIALS; trial += 1) {
    const ranks = draw(0x9e3779b9 ^ Math.imul(trial + 1, 0x85ebca6b));
    const counts = rankCounts(ranks);
    trials.push({ ranks, counts });
    counts.forEach((count, rank) => totals.set(rank, (totals.get(rank) || 0) + count));
  }

  const mean = new Map();
  totals.forEach((total, rank) => mean.set(rank, total / TRIALS));

  let best = trials[0];
  let bestDistance = Infinity;
  trials.forEach((trial) => {
    const distance = countDistance(trial.counts, mean);
    if (distance < bestDistance) {
      best = trial;
      bestDistance = distance;
    }
  });

  return best.ranks;
}
